import { Controller } from "@hotwired/stimulus";

// The bundle picker on the collection detail "preview" step (#custom-packaging-bundle-form).
// Confirm/"do not use" are real <button type="submit" form="custom-packaging-bundle-form">
// elements inside the shared modal partial -- the browser submits the form natively once this
// controller has written the chosen id into the hidden field, so there is no fetch/$http here.
//
// #custom-packaging-bundle-modal is a separate partial, not a descendant of this element, shared
// with the /give/collections picker (custom_packaging_collections_modal_controller.js); only one
// of the two mounts on any given page. Its internals stay a global id lookup rather than targets.
//
// There is deliberately no `nextStep` here: the "Send Collection" button is a DOM sibling of this
// form, so it is served by collection_detail_products_controller.js instead.
export default class extends Controller {
  static get targets() {
    return ["hiddenBundleId"];
  }

  connect() {
    this.modal = document.getElementById("custom-packaging-bundle-modal");
    if (!this.modal) {
      return;
    }

    this.searchBox = this.modal.querySelector("#custom-packaging-bundle-search");
    this.bundleGrid = this.modal.querySelector("#custom-packaging-bundle-grid");
    this.confirmBtn = this.modal.querySelector("#custom-packaging-bundle-confirm");
    this.doNotUseBtn = this.modal.querySelector("#do-not-use-custom-packaging");

    this.pendingBundleId = null;

    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.handleGridClick = this.handleGridClick.bind(this);
    this.handleConfirmClick = this.handleConfirmClick.bind(this);
    this.handleDoNotUseClick = this.handleDoNotUseClick.bind(this);

    // Bootstrap's modal plugin announces via jQuery's `.trigger()`, which a plain
    // addEventListener never sees, so stay on jQuery for this one event.
    $(this.modal).on("show.bs.modal", this.handleShowModal);
    this.searchBox.addEventListener("input", this.handleSearchInput);
    this.bundleGrid.addEventListener("click", this.handleGridClick);
    this.confirmBtn.addEventListener("click", this.handleConfirmClick);
    this.doNotUseBtn.addEventListener("click", this.handleDoNotUseClick);
  }

  disconnect() {
    if (!this.modal) {
      return;
    }

    $(this.modal).off("show.bs.modal", this.handleShowModal);
    this.searchBox.removeEventListener("input", this.handleSearchInput);
    this.bundleGrid.removeEventListener("click", this.handleGridClick);
    this.confirmBtn.removeEventListener("click", this.handleConfirmClick);
    this.doNotUseBtn.removeEventListener("click", this.handleDoNotUseClick);
  }

  handleShowModal() {
    const currentId = this.hiddenBundleIdTarget.value || null;
    this.pendingBundleId = currentId;

    this.bundleGrid.querySelectorAll(".custom-packaging-bundle-card").forEach((card) => {
      card.classList.toggle("selected", String(card.dataset.id) === String(currentId));
    });
    this.confirmBtn.disabled = !this.pendingBundleId;
  }

  // All available bundles are already rendered, so filter them client-side by name.
  handleSearchInput() {
    const query = this.searchBox.value.trim().toLowerCase();
    const cards = Array.from(this.bundleGrid.querySelectorAll(".custom-packaging-bundle-card"));
    let visibleCount = 0;

    cards.forEach((card) => {
      const name = (card.querySelector(".bundle-name")?.textContent || "").toLowerCase();
      const matches = !query || name.indexOf(query) !== -1;
      card.classList.toggle("hidden", !matches);
      if (matches) {
        visibleCount += 1;
      }
    });

    const noResults = this.bundleGrid.querySelector(".custom-packaging-bundle-no-results");
    if (noResults) {
      noResults.classList.toggle("hidden", cards.length === 0 || visibleCount > 0);
    }
  }

  // Only ready bundles with available inventory are selectable.
  handleGridClick(event) {
    const card = event.target.closest(".custom-packaging-bundle-card");
    if (!card || !this.bundleGrid.contains(card)) {
      return;
    }
    if (card.dataset.selectable !== "true") {
      return;
    }

    card.classList.add("selected");
    Array.from(card.parentElement.children).forEach((sibling) => {
      if (sibling !== card) {
        sibling.classList.remove("selected");
      }
    });
    this.pendingBundleId = card.dataset.id;
    this.confirmBtn.disabled = false;
    this.revealFooterActions();
  }

  // With a long grid the footer actions can sit below the fold, so bring them into view on
  // every selection. A no-op when they are already visible.
  revealFooterActions() {
    const footer = this.modal.querySelector(".modal-footer");
    if (footer) {
      footer.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  // Confirm/clear just write the choice into the hidden field and let the button's native
  // submit send a real PATCH to /give -- the server assigns the bundle, swaps the build's
  // collection for its CP/non-CP counterpart (see Gallery::CUSTOM_PACKAGING_COLLECTION_PAIRS),
  // and redirects to that collection's preview page, so there's no client-side state to keep
  // in sync.
  handleConfirmClick() {
    if (!this.pendingBundleId) {
      return;
    }
    this.hiddenBundleIdTarget.value = this.pendingBundleId;
  }

  handleDoNotUseClick() {
    this.hiddenBundleIdTarget.value = "";
  }

  clearSelection() {
    this.hiddenBundleIdTarget.value = "";
  }
}
